import { FuseNavigationItem } from '@fuse/components/navigation';
import { Feature } from 'app/api/models';
import { PERMISSIONS as P, PermissionCode } from 'app/core/services/permissions/permissions';

/**
 * Settings is one page of many panels, each with its own permission (settings.component.ts). Holding
 * any one of them is a reason to open the page. Shared by the sidebar item and the route.
 */
export const SETTINGS_PERMISSIONS: readonly PermissionCode[] = [
    P.settingsManage, P.hrManage, P.usersManage, P.rolesView, P.rolesManage,
    P.accountingView, P.systemFeatures, P.systemDatabase,
];

/**
 * Which permission puts which item in the sidebar. Holding ANY of the codes listed is enough.
 *
 * - Keyed on the ids in `mock-api/common/navigation/data.ts`.
 * - A dotted id falls back to its parents, so `hire` covers `hire.calendar` and everything else
 *   beneath it. Name a child only to make it differ.
 * - An id that matches nothing here is shown to everybody signed in (Dashboard, Help Center).
 * - Groups are never listed for their own sake: a group shows when any child does.
 *
 * ROUTES use the same codes: see `requirePermission(...)` in `app.routes.ts`. Keep the two in
 * step, or somebody gets a menu item that bounces them, or a page with no way to reach it.
 *
 * Adding a module: one line here, one on its route, and the code in `Permissions.cs`.
 */
export const NAV_PERMISSIONS: Readonly<Record<string, readonly PermissionCode[]>> = {
    'project-management': [P.projectsView],

    'crm': [P.crmView],
    'crm.contacts': [P.customersView],
    'crm.reporting': [P.reportingView],

    'hire': [P.hireView],

    // The bench: what an engineer works from.
    'calibration.my-dashboard': [P.calibrationView],
    'calibration.workshop-dashboard': [P.calibrationView],
    // The office side: booking in, servicing, dispatch.
    'calibration.admin-dashboard': [P.calibrationOffice],
    'calibration.engineer-dashboard': [P.calibrationOffice],
    'calibration.servicing': [P.calibrationOffice],
    'calibration.settings': [P.calibrationSettings],

    'part-register': [P.partsView],
    'product-register': [P.productsView],
    'boms': [P.productsView],
    'change-notes': [P.productsView],
    'suppliers': [P.suppliersView],
    'stock-settings': [P.stockManage],
    'goods': [P.stockView, P.partsView],
    'stock': [P.stockView, P.partsView],

    'marine': [P.marineView],
    'marine.settings': [P.marineSettings],

    'purchase-orders': [P.purchasingView],
    'purchase-order-settings': [P.purchasingSettings],

    'sales-orders': [P.salesView],
    'internal-stock-orders': [P.stockOrdersView],
    'production': [P.productionView],

    'hr': [P.hrView],
    'reports': [P.reportingView],
    'form-builder': [P.settingsManage],

    'settings': SETTINGS_PERMISSIONS,
};

/** Clocking in is decided per person (a flag on their record) as well as by permission. */
export const CLOCKING_NAV_ID = 'calibration.clocking-in';

/**
 * The company-wide module switches (Settings > Features). These are NOT permissions: they say
 * what this installation of Nexus has at all, for everybody, and they are applied as well as the
 * permission check, never instead of it.
 */
export const FEATURE_NAV_IDS: Readonly<Partial<Record<Feature, string>>> = {
    [Feature.CRM_Contacts]: 'crm.contacts',
    [Feature.CRM_Leads]: 'crm.leads',
    [Feature.CRM_Pipeline]: 'crm.pipeline',
    [Feature.CRM_Emails]: 'crm.emails',
    [Feature.CRM_Reporting]: 'crm.reporting',
    [Feature.CRM_SalesOrders]: 'crm.sales-orders',

    [Feature.HIRE_Customers]: 'hire.customers',
    [Feature.HIRE_Dashboard]: 'hire.dashboard',
    [Feature.HIRE_HireOrders]: 'hire.hire-orders',
    [Feature.HIRE_Calendar]: 'hire.calendar',
    [Feature.HIRE_Assets]: 'hire.assets',
    [Feature.HIRE_Invoicing]: 'hire.invoicing',
    [Feature.HIRE_AssetBoard]: 'hire.asset-board',

    [Feature.PROJ_Projects]: 'project-management.projects',

    [Feature.CLOCK_ClockingIn]: CLOCKING_NAV_ID,
    [Feature.CALIB_MyDash]: 'calibration.my-dashboard',
    [Feature.CALIB_AdminDash]: 'calibration.admin-dashboard',
    [Feature.CALIB_WorkshopDash]: 'calibration.workshop-dashboard',
    [Feature.CALIB_EngineerDash]: 'calibration.engineer-dashboard',
    [Feature.CALIB_Servicing]: 'calibration.servicing',
    [Feature.CALIB_Settings]: 'calibration.settings',
    [Feature.CALIB_Customers]: 'calibration.customers',
    [Feature.CALIB_Form10a]: 'calibration.form-10a',

    [Feature.PROD_Production]: 'production',
};

export function permissionsForNavItem(id: string | undefined): readonly PermissionCode[] {
    for (let key = id ?? ''; key; key = key.includes('.') ? key.slice(0, key.lastIndexOf('.')) : '') {
        const codes = NAV_PERMISSIONS[key];
        if (codes) return codes;
    }
    return [];
}

export interface NavigationAccess {
    /** What the signed-in person holds. */
    codes: ReadonlySet<string>;
    /** The features switched ON for this installation. */
    enabledFeatures: readonly Feature[];
    /** Their record says they clock in and out. */
    canClockInOut: boolean;
}

/**
 * The sidebar for one person. Pure: same inputs, same menu, and it never touches the items it is
 * given (the source arrays are shared, module-level data).
 */
export function filterNavigation(items: readonly FuseNavigationItem[], access: NavigationAccess): FuseNavigationItem[] {
    const switchedOff = new Set(
        Object.entries(FEATURE_NAV_IDS)
            .filter(([feature]) => !access.enabledFeatures.includes(+feature as Feature))
            .map(([, navId]) => navId)
    );

    const allowed = (item: FuseNavigationItem): boolean => {
        if (switchedOff.has(item.id)) return false;

        if (item.id === CLOCKING_NAV_ID)
            return access.canClockInOut || access.codes.has(P.clockingView);

        const required = permissionsForNavItem(item.id);
        return !required.length || required.some((code) => access.codes.has(code));
    };

    const walk = (list: readonly FuseNavigationItem[]): FuseNavigationItem[] =>
        (list ?? [])
            .filter((item) => !!item)
            .map((item) => (item.children ? { ...item, children: walk(item.children) } : item))
            .filter((item) =>
                // A group stands or falls with its children; anything else on its own permission.
                item.children ? item.children.length > 0 : allowed(item)
            );

    return walk(items);
}
